import { Button, ManageableItemsList, SkeletonLoader } from '@payaca/ambrosia';
import RouterTabs, { TabPanel } from '@payaca/components/routerTabs/RouterTabs';
import { FC, useReducer } from 'react';

import useUpdateMaterialsListMaterials from '@/api/mutations/materialsList/useUpdateMaterialsListMaterials';
import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import projectKeys from '@/api/queries/project/keyFactory';
import useGetProjectMaterialsListSummaryItems, {
  MaterialPurchaseRecord,
} from '@/api/queries/project/useGetProjectMaterialListSummaryItems';
import useGetProjectMaterialsListAuditLogs from '@/api/queries/project/useGetProjectMaterialsListAuditLogs';
import useGetProjectMaterialsListMaterials from '@/api/queries/project/useGetProjectMaterialsListMaterials';
import BuildMaterialsListSidebar from '@/ui/components/buildMaterialsListSidebar/BuildMaterialsListSidebar';
import PopulateDealMaterialsListControl from '@/ui/components/populateDealMaterialsListControl/PopulateDealMaterialsListControl';
import ViewMaterialSidebar from '@/ui/pages/listedMaterialsPage/components/ViewMaterialSidebar';

import { useDeal } from '@payaca/store/hooks/appState';
import * as materialsListActions from '@payaca/store/materialsList/materialsListActions';
import { requestDeleteUpload } from '@payaca/store/uploads/uploadsActions';
import { Upload } from '@payaca/types/uploadTypes';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { CreatePurchaseOrderControl } from './MaterialsPanel/CreatePurchaseOrderModal';
import { DeleteMaterialPurchaseRecordModal } from './MaterialsPanel/DeleteMaterialPurchaseRecordModal';
import {
  DeleteMaterialListMaterial,
  DeleteMaterialsListMaterialModal,
} from './MaterialsPanel/DeleteMaterialsListMaterialModal';
import {
  EditMaterialListMaterialQuantity,
  EditMaterialsListMaterialModal,
} from './MaterialsPanel/EditMaterialsListMaterialModal';
import { HistoryPanel } from './MaterialsPanel/HistoryPanel';
import { ImportMaterialPurchasesFromReceiptDocumentUploadSidebar } from './MaterialsPanel/ImportMaterialPurchasesFromReceiptDocumentUploadSidebar';
import {
  useAuditLogsInputPaginationState,
  usePaginationState,
  useSummaryItemsInputPaginationState,
} from './MaterialsPanel/materialsPanelHooks';
import { OrderedPanel } from './MaterialsPanel/OrderedPanel';
import { PurchaseOrdersPanel } from './MaterialsPanel/PurchaseOrdersPanel';
import { ReceiptDocumentUploadModal } from './MaterialsPanel/ReceiptDocumentUploadModal';
import { RecordPurchaseModal } from './MaterialsPanel/RecordPurchaseModal';
import { SummaryPanel } from './MaterialsPanel/SummaryPanel';
import { ToOrderPanel } from './MaterialsPanel/ToOrderPanel';

interface Props {
  dealId: number;
}

const PAGE_SIZE = 100; // Setting really high until we have search + filtering

export const MaterialsPanel: FC<Props> = ({ dealId }: Props) => {
  const [state, dispatch] = useReducer(modalReducer, { activeModal: null });

  const deal = useDeal(dealId);

  const accountHasAiFeature = useMyAccountHasFeature('useAi');

  const toOrderInputPaginationState = useSummaryItemsInputPaginationState(
    PAGE_SIZE,
    { statuses: ['TO_ORDER', 'ORDER_DRAFTED'] }
  );
  const orderedInputPaginationState = useSummaryItemsInputPaginationState(
    PAGE_SIZE,
    { statuses: ['ORDERED', 'PURCHASED'] }
  );
  const {
    pagination: summaryPagination,
    setCurrentPage: setSummaryCurrentPage,
  } = usePaginationState(PAGE_SIZE);
  const auditLogsInputPaginationState =
    useAuditLogsInputPaginationState(PAGE_SIZE);

  const queryClient = useQueryClient();
  const { materialsListOrderSummaryItems: toOrderItems } =
    useGetProjectMaterialsListSummaryItems(
      deal?.publicId,
      {
        searchTerm: toOrderInputPaginationState.input.searchTerm,
        statuses: toOrderInputPaginationState.input.statuses,
        sortBy: toOrderInputPaginationState.input.sortBy,
        sortDirection: toOrderInputPaginationState.input.sortDirection,
      },
      {
        limit: toOrderInputPaginationState.pagination.limit,
        offset: toOrderInputPaginationState.pagination.offset,
      }
    );

  const { materialsListOrderSummaryItems: orderedItems } =
    useGetProjectMaterialsListSummaryItems(
      deal?.publicId,
      {
        searchTerm: orderedInputPaginationState.input.searchTerm,
        statuses: orderedInputPaginationState.input.statuses,
        sortBy: orderedInputPaginationState.input.sortBy,
        sortDirection: orderedInputPaginationState.input.sortDirection,
      },
      {
        limit: orderedInputPaginationState.pagination.limit,
        offset: orderedInputPaginationState.pagination.offset,
      }
    );
  const { auditLogs } = useGetProjectMaterialsListAuditLogs(
    deal?.publicId,
    {
      searchTerm: auditLogsInputPaginationState.input.searchTerm,
      actingUserIds: auditLogsInputPaginationState.input.actingUserIds,
    },
    {
      limit: auditLogsInputPaginationState.pagination.limit,
      offset: auditLogsInputPaginationState.pagination.offset,
    }
  );

  const { data, materialsListMaterials } = useGetProjectMaterialsListMaterials(
    deal?.publicId,
    {
      limit: summaryPagination.limit,
      offset: summaryPagination.offset,
    }
  );

  const materialsListId =
    (data?.projectById && +data.projectById.materialsList.id) || null;
  const materialsListPublicId =
    (data?.projectById && data.projectById.materialsList.publicId) || null;

  const reduxDispatch = useDispatch();

  const { mutateAsync: onAddMaterials } = useUpdateMaterialsListMaterials(
    deal?.publicId,
    {
      onSettled: () => {
        if (materialsListId) {
          reduxDispatch(
            materialsListActions.requestGetMaterialsListWithRelatedEntities(
              materialsListId
            )
          );
        }
      },
    }
  );

  const handleInvalidQueries = () => {
    if (deal?.publicId) {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.materialsList(deal.publicId),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.profitBreakdown(deal.publicId),
      });
    }
  };

  if (!materialsListMaterials || !materialsListId) {
    return <SkeletonLoader className="h-20 rounded-lg" />;
  }

  const selectedMaterialsListMaterial =
    state.activeModal === 'recordPurchase' &&
    toOrderItems?.items.find(
      (item) => item.materialsListMaterial.id === state.materialListMaterialId
    )?.materialsListMaterial;

  const tabPanels: TabPanel[] = [
    {
      label: 'Summary',
      slug: 'summary',
      render: () =>
        materialsListMaterials ? (
          <SummaryPanel
            summaryItems={materialsListMaterials}
            onPageChange={setSummaryCurrentPage}
            onMaterialView={(materialId) =>
              dispatch({
                type: 'openViewMaterial',
                payload: { materialId },
              })
            }
          />
        ) : null,
    },
    {
      label: 'To order',
      slug: 'to-order',
      render: () => (
        <ToOrderPanel
          inputPaginationState={toOrderInputPaginationState}
          summaryItems={toOrderItems}
          projectId={dealId}
          onRequestEditMaterialsListMaterial={(mlm) => {
            dispatch({
              type: 'openEditMaterialsListMaterial',
              payload: { materialsListMaterial: mlm },
            });
          }}
          onRequestDeleteMaterialsListMaterial={(mlm) => {
            dispatch({
              type: 'openDeleteMaterialsListMaterial',
              payload: { materialsListMaterial: mlm },
            });
          }}
          onRecordPurchase={(materialListMaterialId) =>
            dispatch({
              type: 'openRecordPurchase',
              payload: { materialListMaterialId },
            })
          }
          onMaterialView={(materialId) =>
            dispatch({
              type: 'openViewMaterial',
              payload: { materialId },
            })
          }
        />
      ),
    },
    {
      label: 'Ordered',
      slug: 'ordered',
      render: () => (
        <OrderedPanel
          summaryItems={orderedItems}
          projectId={dealId}
          inputPaginationState={orderedInputPaginationState}
          onRequestDeleteMaterialPurchaseRecord={(row) => {
            if (row.__typename == 'MaterialsListOrderSummaryPurchasedItem') {
              dispatch({
                type: 'openDeleteMaterialPurchaseRecord',
                payload: {
                  materialPurchaseRecord: row.materialPurchaseRecord,
                },
              });
            }
          }}
          onMaterialView={(materialId) =>
            dispatch({
              type: 'openViewMaterial',
              payload: { materialId },
            })
          }
        />
      ),
    },
    {
      label: 'Purchase Orders',
      slug: 'purchase-orders',
      render: () =>
        deal?.publicId && (
          <PurchaseOrdersPanel
            projectId={deal.publicId}
            materialsListId={materialsListId}
            onUpdate={handleInvalidQueries}
          />
        ),
    },
    {
      label: 'History',
      slug: 'history',
      render: () => (
        <HistoryPanel
          projectId={dealId}
          inputPaginationState={auditLogsInputPaginationState}
          auditLogs={auditLogs}
          onMaterialView={(materialId) =>
            dispatch({
              type: 'openViewMaterial',
              payload: { materialId },
            })
          }
        />
      ),
    },
  ];

  return (
    <ManageableItemsList>
      <ManageableItemsList.HeaderBar
        heading="Materials"
        buttons={
          <>
            {accountHasAiFeature && (
              <Button
                onClick={() => dispatch({ type: 'openUploadReceiptDocument' })}
                size={'sm'}
                variant="white"
              >
                Upload Invoice
              </Button>
            )}
            <PopulateDealMaterialsListControl dealId={dealId} />
            <CreatePurchaseOrderControl
              materialsListId={materialsListId}
              projectId={dealId}
            />
            <Button
              onClick={() =>
                dispatch({ type: 'openCreateMaterialsListMaterial' })
              }
              size={'sm'}
            >
              Add Materials
            </Button>
          </>
        }
      />
      <RouterTabs panels={tabPanels}>
        <RouterTabs.Nav
          defaultTabSlug="summary"
          className="p-2 px-4"
          onChange={() => {
            setSummaryCurrentPage(1);
            toOrderInputPaginationState.reset();
            orderedInputPaginationState.reset();
            auditLogsInputPaginationState.reset();
          }}
        />

        <RouterTabs.Panels />
      </RouterTabs>

      <BuildMaterialsListSidebar
        title="Add Materials"
        showPriceIncludingTax={false}
        emptyStatePromptText="Add required Materials"
        primaryActionText="Add to Project"
        warnUserContent={{
          body: "You have selected Materials that you haven't added to the Project.",
        }}
        isOpen={state.activeModal === 'createMaterialsListMaterial'}
        onClose={() => dispatch({ type: 'closeModal' })}
        onAddMaterials={async (materials) => {
          await onAddMaterials({
            materialsListId: materialsListId.toString(),
            materialsListMaterials: materials.map((material) => ({
              materialId: material.id,
              quantityChange: {
                relative: material.quantity,
              },
            })),
          });
        }}
        projectId={dealId}
      />
      {selectedMaterialsListMaterial && (
        <RecordPurchaseModal
          materialsListId={materialsListId}
          isOpen
          materialAndSuppliers={selectedMaterialsListMaterial}
          onClose={() => dispatch({ type: 'closeModal' })}
          onSuccess={() => {
            handleInvalidQueries();
            dispatch({ type: 'closeModal' });
          }}
        />
      )}
      {state.activeModal === 'deleteMateralsListMaterial' && (
        <DeleteMaterialsListMaterialModal
          isOpen={true}
          onClose={() => dispatch({ type: 'closeModal' })}
          materialsListMaterial={state.materialsListMaterial}
          onDeleteSuccess={() => {
            handleInvalidQueries();
            dispatch({ type: 'closeModal' });
          }}
        />
      )}
      {state.activeModal === 'uploadReceiptDocument' && (
        <ReceiptDocumentUploadModal
          isOpen={true}
          onClose={() => dispatch({ type: 'closeModal' })}
          onUploadSuccess={(upload) => {
            dispatch({
              type: 'openImportMaterialPurchasesFromReceiptDocumentUpload',
              payload: { upload },
            });
          }}
        />
      )}
      {state.activeModal ===
        'importMaterialPurchasesFromReceiptDocumentUpload' &&
        materialsListPublicId && (
          <ImportMaterialPurchasesFromReceiptDocumentUploadSidebar
            uploadId={state.upload.publicId}
            materialsListId={materialsListPublicId}
            isOpen={true}
            onClose={() => {
              reduxDispatch(requestDeleteUpload(state.upload.id));
              dispatch({ type: 'closeModal' });
            }}
            onSuccess={() => {
              handleInvalidQueries();
              dispatch({ type: 'closeModal' });
            }}
          />
        )}
      {state.activeModal === 'editMateralsListMaterial' && (
        <EditMaterialsListMaterialModal
          isOpen={true}
          onClose={() => dispatch({ type: 'closeModal' })}
          materialsListMaterial={state.materialsListMaterial}
          onPersistSuccess={() => {
            handleInvalidQueries();
            dispatch({ type: 'closeModal' });
          }}
        />
      )}
      {state.activeModal === 'deleteMaterialPurchaseRecord' && (
        <DeleteMaterialPurchaseRecordModal
          isOpen={true}
          onClose={() => dispatch({ type: 'closeModal' })}
          materialPurchaseRecord={state.materialPurchaseRecord}
          onDeleteSuccess={() => {
            handleInvalidQueries();
            dispatch({ type: 'closeModal' });
          }}
        />
      )}
      <ViewMaterialSidebar
        isOpen={state.activeModal === 'viewMaterial'}
        materialId={
          state.activeModal === 'viewMaterial' ? state.materialId : undefined
        }
        projectId={dealId}
        onClose={() => dispatch({ type: 'closeModal' })}
      />
    </ManageableItemsList>
  );
};

type ModalState =
  | {
      activeModal: 'recordPurchase';
      materialListMaterialId: string;
    }
  | {
      activeModal: 'viewMaterial';
      materialId: string;
    }
  | {
      // Ok so its not strictly a modal but it's a modal-like thing
      activeModal: 'createMaterialsListMaterial';
    }
  | {
      activeModal: 'deleteMateralsListMaterial';
      materialsListMaterial: DeleteMaterialListMaterial;
    }
  | {
      activeModal: 'deleteMaterialPurchaseRecord';
      materialPurchaseRecord: MaterialPurchaseRecord;
    }
  | {
      activeModal: 'editMateralsListMaterial';
      materialsListMaterial: EditMaterialListMaterialQuantity;
    }
  | {
      activeModal: null;
    }
  | {
      activeModal: 'uploadReceiptDocument';
    }
  | {
      activeModal: 'importMaterialPurchasesFromReceiptDocumentUpload';
      upload: Upload;
    };

type ModalAction =
  | {
      type: 'openViewMaterial';
      payload: {
        materialId: string;
      };
    }
  | {
      type: 'openRecordPurchase';
      payload: {
        materialListMaterialId: string;
      };
    }
  | {
      type: 'openCreateMaterialsListMaterial';
    }
  | {
      type: 'openDeleteMaterialsListMaterial';
      payload: {
        materialsListMaterial: DeleteMaterialListMaterial;
      };
    }
  | {
      type: 'openDeleteMaterialPurchaseRecord';
      payload: {
        materialPurchaseRecord: MaterialPurchaseRecord;
      };
    }
  | {
      type: 'openEditMaterialsListMaterial';
      payload: {
        materialsListMaterial: EditMaterialListMaterialQuantity;
      };
    }
  | {
      type: 'openUploadReceiptDocument';
    }
  | {
      type: 'openImportMaterialPurchasesFromReceiptDocumentUpload';
      payload: {
        upload: Upload;
      };
    }
  | {
      type: 'closeModal';
    };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'openViewMaterial':
      return {
        ...state,
        activeModal: 'viewMaterial',
        materialId: action.payload.materialId,
      };
    case 'openCreateMaterialsListMaterial':
      return {
        ...state,
        activeModal: 'createMaterialsListMaterial',
      };
    case 'openRecordPurchase':
      return {
        ...state,
        activeModal: 'recordPurchase',
        materialListMaterialId: action.payload.materialListMaterialId,
      };
    case 'openDeleteMaterialsListMaterial':
      return {
        ...state,
        activeModal: 'deleteMateralsListMaterial',
        materialsListMaterial: action.payload.materialsListMaterial,
      };
    case 'openDeleteMaterialPurchaseRecord':
      return {
        ...state,
        activeModal: 'deleteMaterialPurchaseRecord',
        materialPurchaseRecord: action.payload.materialPurchaseRecord,
      };
    case 'openEditMaterialsListMaterial':
      return {
        ...state,
        activeModal: 'editMateralsListMaterial',
        materialsListMaterial: action.payload.materialsListMaterial,
      };
    case 'openUploadReceiptDocument':
      return {
        ...state,
        activeModal: 'uploadReceiptDocument',
      };
    case 'openImportMaterialPurchasesFromReceiptDocumentUpload':
      return {
        ...state,
        activeModal: 'importMaterialPurchasesFromReceiptDocumentUpload',
        upload: action.payload.upload,
      };
    case 'closeModal':
      return {
        activeModal: null,
      };
    default:
      return state;
  }
};
